<template>
  <div
    class="mouseover-teaser"
    :class="{
      [backgroundAnimationClass]: mouseOver,
    }"
    :style="backgroundStyle"
    @mouseover="mouseOver = true"
    @mouseleave="mouseOver = false"
  >
    <slot name="background">
      <CdnImage
        v-if="backgroundImage"
        class="mouseover-teaser__background"
        :alt="backgroundImage.alt"
        :src="backgroundImage.src"
        :src-sm="backgroundImage.srcSm"
        :src-md="backgroundImage.srcMd"
        :src-lg="backgroundImage.srcLg"
        :src-xl="backgroundImage.srcXl"
        :lazy="backgroundImage.lazy"
        :sizes="backgroundImage.sizes"
        :do-not-convert="!backgroundImage.sizes"
      />
    </slot>
    <nuxt-link
      :to="link"
      class="link link--unstyled mouseover-teaser__container"
    >
      <header
        v-if="$slots.header"
        class="mouseover-teaser__header text--dark"
      >
        <slot
          :mouse-over="mouseOver"
          name="header"
        />
      </header>
      <slot :mouse-over="mouseOver" />
      <div
        class="mouseover-teaser__image"
        :class="{
          'mouseover-teaser--scale': mouseOver,
        }"
      >
        <slot
          name="image"
          :mouse-over="mouseOver"
        />
      </div>
      <div
        class="mouseover-teaser__content"
        :class="{
          'mouseover-teaser__content__hover': mouseOver,
        }"
      >
        <slot
          name="mouseOver"
          :mouse-over="mouseOver"
        />
      </div>
    </nuxt-link>
  </div>
</template>

<script setup lang="ts">
import { ref, withDefaults } from 'vue';
import CdnImage from '@/components/cdn-image.vue';

interface BackgroundImage {
  alt?: string;
  src?: string;
  srcSm?: string;
  srcMd?: string;
  srcLg?: string;
  srcXl?: string;
  lazy?: boolean;
  sizes?: string;
}

export type MouseOverTeaserProps = {
  backgroundAnimationClass?: string;
  backgroundImage?: BackgroundImage;
  backgroundStyle?: string;
  link?: string;
};

const props = withDefaults(defineProps<MouseOverTeaserProps>(), {
  backgroundAnimationClass: 'mouseover-teaser--rounded',
  backgroundStyle: undefined,
  backgroundImage: undefined,
  link: '/',
});

const mouseOver = ref(false);
</script>

<style scoped lang="scss">
.mouseover-teaser {
  transition: border-radius 0.3s ease-in-out;
  position: relative;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 100%;

  &__background,
  &__container {
    grid-column: 1;
    grid-row: 1;
  }

  &__background {
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
    transition: transform 0.3s ease-in-out;

    :deep(img) {
      object-fit: cover;
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__container {
    height: 100%;
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: auto 1fr;
    justify-content: center;
    align-items: center;
    gap: 0;

    &:focus-visible {
      outline-offset: -2px;
      outline: var(--focus-outline);
    }

    &:hover {
      text-decoration: none;
    }
  }

  &__header {
    position: relative;
    grid-column: 1;
    grid-row: 1;
  }

  &__image,
  &__content {
    grid-column: 1;
    grid-row: 2;
  }

  &__image {
    transition: transform 0.3s ease-in-out;
    width: 80%;
    height: 80%;
    margin: auto;
    text-align: center;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;

    :deep(img) {
      max-height: 100%;
    }
  }

  &__content {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  // different animations types
  &--scale {
    transform: scale(1.1);
  }

  &--rounded {
    border-radius: 5%;
  }
}
</style>
